import React, { useEffect, useState } from 'react';
import { useSlateStatic, useSlateSelector } from 'slate-react';
import { Editor, Element } from 'slate';
import defaultToolbarGroups from './toolbarGroups';
import ColorPicker from '../Elements/ColorPicker/ColorPicker';
import LinkButton from '../Elements/Link/LinkButton';
import Embed from '../Elements/Embed/Embed';
import TableSelector from '../Elements/Table/TableSelector';
import Id from '../Elements/ID/ID';
import TableContextMenu from '../Elements/TableContextMenu/TableContextMenu';
import styles from './Toolbar.module.scss';
import { commentsIcon, progressAutosavedGray, ClockCounterDark } from '../../../assets/hostedassets';
import closeIcon from '../../_custom/Modal/close.svg';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { closeCommentsThread, setShowCommentsThread } from '../../../store/commentsThread/commentsThread_actions';
import { COMMENTS_THREAD_SOURCES } from '../../../store/commentsThread/commentsThread_reducer';
import clsx from 'clsx';
import LoadingSpinner from '../../_custom/LoadingSpinner/LoadingSpinner';
import { toast } from 'react-toastify';
import { Mixpanel } from '../../../utils/mixpanel';
import { getSubdomain } from '../../../utils/utils';
import { AICA_VIEW_COMMENT } from '../../../utils/constants/mixpanelEvents/investorEvents';
import { resetAllSelections } from '../Elements/Table/components/tableSelectionUtils';
import { BlockButton, MarkButton, Dropdown } from './ToolbarComponents';
import { activeMark } from '../utils/SlateUtilityFunctions';

const Toolbar = (props: any) => {
    const { icon, savingData, handleCodeToText, onClose, onOpenHistory, onToggleHistory, historyOpen, readOnly = false } = props;
    const editor = useSlateStatic();
    const dispatch = useAppDispatch();
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const onePagerData = useAppSelector((state) => state.investor.onePager);
    const engagementLetterData = useAppSelector((state) => state.investor.engagementLetter);
    const userData = useAppSelector((state) => state.user.userData);
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);

    // Optimized isTable check using useSlateSelector to only trigger re-renders when table state changes
    const isTable = useSlateSelector((state) => {
        if (!state.selection) return false;
        const [node] = Editor.nodes(state, {
            match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table',
        });
        return !!node;
    });

    const [toolbarGroups, setToolbarGroups] = useState<any[]>(defaultToolbarGroups);

    const assesseeOrgId = onePagerData?.open
        ? onePagerData?.assesseeOrgId
        : engagementLetterData?.open
        ? engagementLetterData?.orgId
        : null;
    const source = (
        onePagerData?.open
            ? COMMENTS_THREAD_SOURCES.ONE_PAGER
            : engagementLetterData?.open
            ? COMMENTS_THREAD_SOURCES.ENGAGEMENT_LETTER
            : null
    ) as any;

    useEffect(() => {
        // Filter out the groups which are not allowed to be inserted when a table is in focus.
        let filteredGroups: any[] = [...defaultToolbarGroups];
        if (isTable) {
            filteredGroups = toolbarGroups.map((grp: any) =>
                grp.filter(
                    (element: any) =>
                        //groups that are not supported inside the table
                        !['codeToText'].includes(element.type),
                ),
            );
            filteredGroups = filteredGroups.filter((elem: any) => elem.length);
        }
        setToolbarGroups(filteredGroups);
    }, [isTable]);

    const showHideCommentsThread = () => {
        if (commentsThreadState.showCommentsThread) {
            dispatch(closeCommentsThread());
        } else {
            if (!assesseeOrgId) {
                toast.error('Failed to initialise comments thread');
                return;
            }
            dispatch(
                setShowCommentsThread({
                    source,
                    assesseeOrgId,
                }),
            );

            Mixpanel.track(AICA_VIEW_COMMENT, {
                userRole: userData?.accountType,
                pnoPlatform: getSubdomain(),
                pnoId: domainMetadata?.investorOrgId,
                userId: userData?.userId,
                currentUrl: window.location.href,
                accessibility: source,
                organizationId: assesseeOrgId,
            });
        }
    };

    return (
        <div className={styles.Toolbar}>
            <div className={styles.ToolbarLeft}>
                {!readOnly &&
                    toolbarGroups.map((group, index) => (
                        <span key={index} className={styles.ToolbarGrp}>
                            {group.map((element: any) => {
                                switch (element.type) {
                                    case 'block':
                                        return <BlockButton key={element.id} {...element} />;
                                    case 'mark':
                                        return <MarkButton key={element.id} {...element} />;
                                    case 'dropdown':
                                        return <Dropdown key={element.id} {...element} />;
                                    case 'link':
                                        return <LinkButton key={element.id} editor={editor} icon={element?.icon} />;
                                    case 'embed':
                                        return <Embed key={element.id} format={element.format} editor={editor} icon={element?.icon} />;
                                    case 'color-picker':
                                        return <ColorPicker key={element.id} format={element.format} editor={editor} iconHeight="20px" />;
                                    case 'table':
                                        return <TableSelector key={element.id} editor={editor} icon={element?.icon} />;
                                    case 'id':
                                        return <Id editor={editor} />;
                                    default:
                                        return null;
                                }
                            })}
                        </span>
                    ))}
            </div>

            <div className={styles.ToolbarRight}>
                {!commentsThreadState.showCommentsThread && <img src={progressAutosavedGray} alt={'p'} width={'140px'} />}
                {onToggleHistory && (
                    <div
                        className={clsx(styles.HistoryIconContainer, {
                            [styles.HistoryIconContainer_HoverActive]: historyOpen,
                        })}
                        onClick={onToggleHistory}
                        title="View History"
                    >
                        <img src={ClockCounterDark} alt="history" height={'24px'} width={'24px'} />
                    </div>
                )}
                <div
                    className={clsx(styles.CommentsIconContainer, {
                        [styles.CommentsIconContainer_HoverActive]:
                            commentsThreadState.showCommentsThread && !commentsThreadState.closingCommentsThread,
                    })}
                    onClick={showHideCommentsThread}
                >
                    <img src={commentsIcon} alt="comments" height={'25px'} />
                </div>
                {!historyOpen &&
                    (savingData ? (
                        <LoadingSpinner color="#BCBCBC" height="18px" style={{ margin: '0' }} />
                    ) : (
                        <img
                            onClick={() => {
                                resetAllSelections(editor);
                                onClose?.();
                            }}
                            className={styles.CloseIcon}
                            src={closeIcon}
                            alt={'x'}
                            height={'14px'}
                        />
                    ))}
            </div>

            {/* @ts-ignore */}
            <TableContextMenu editor={editor} />
        </div>
    );
};

export default Toolbar;
